import { useEffect, useImperativeHandle, useRef } from 'react';
import * as Sentry from '@sentry/react';
import fileDownload from 'js-file-download';
import { QR_IMAGE, QR_IMAGE_FONTS, drawTableQrPoster, qrModules, tableQrFileName } from './tableQr';

/**
 * Saves a table's QR code image. `ref.current.download(table)` draws the QR code from the link's
 * module matrix (error correction M, 4-module quiet zone, whole pixels per module), draws the image
 * and saves the PNG. It resolves to whether the file was saved, or to null while another image is
 * still being made (one at a time) or when the list goes away first. The image uses the names as
 * they were at the click. A failure is reported to Sentry; the list says so in a toast.
 */
export default function TableQrRenderer({ ref, restaurantName, tableLabel }) {
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const namesRef = useRef({ restaurantName, tableLabel });
  namesRef.current = { restaurantName, tableLabel };

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      download: async (table) => {
        if (busyRef.current) {
          return null;
        }

        busyRef.current = true;
        const names = namesRef.current;

        try {
          const blob = await makeImage(table, names);

          // A download still running when the list goes away saves nothing, and isn't an error.
          if (!mountedRef.current) {
            return null;
          }

          fileDownload(blob, tableQrFileName(table.name, table.id), 'image/png');

          return true;
        } catch (error) {
          Sentry.captureException(error, {
            tags: { feature: 'table_ordering', action: 'download_qr' },
            extra: { tableId: table?.id },
          });

          return mountedRef.current ? false : null;
        } finally {
          busyRef.current = false;
        }
      },
    }),
    [],
  );

  return null;
}

async function makeImage(table, { restaurantName, tableLabel }) {
  // Encoded before anything is awaited: the image keeps this table's code whatever is clicked next.
  const modules = qrModules(table.link);

  // The dashboard's font (Inter), with a sans-serif fallback should it not have loaded.
  const fontFamily = `${window.getComputedStyle(document.body).fontFamily || 'Inter'}, 'Helvetica Neue', Arial, sans-serif`;

  // The canvas draws with whatever font is loaded, so load the weights it uses first.
  if (document.fonts?.load) {
    await Promise.all(QR_IMAGE_FONTS.map((font) => document.fonts.load(`${font} ${fontFamily}`))).catch(() => null);
  }

  const canvas = document.createElement('canvas');
  canvas.width = QR_IMAGE.width;
  canvas.height = QR_IMAGE.height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Table QR image: no 2D canvas context');
  }

  drawTableQrPoster(ctx, { modules, restaurantName, tableName: table.name, tableLabel, fontFamily });

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });

  if (!blob) {
    throw new Error('Table QR image: the canvas gave no PNG');
  }

  return blob;
}
